import { Vector2D } from '../types/common';
import { Rectangle } from 'ark-common';
import { popupTitlebarHeight, popupMovableBoundsMargin } from '../constants';

export enum FitStyle {
  FitScreen,
  FitWidth,
  FitHeight,
}

export enum SheetLayout {
  Fixed,
  Reflowable,
}

export enum ScrollBase {
  Center,
  LeftIfLargerThanScreen,   // 画面からはみ出る場合は、左を起点にする
  RightIfLargerThanScreen,  // 画面からはみ出る場合は、右を起点にする
}

interface SheetLayoutReflowableDisableZooming {
  readonly layout: SheetLayout.Reflowable;
  readonly fitStyle: FitStyle.FitScreen;
}

interface SheetLayoutReflowable {
  readonly layout: SheetLayout.Reflowable;
  readonly enableMargin: boolean; // XXX : ここで良いのかな...?
  readonly fitStyle: FitStyle.FitWidth | FitStyle.FitHeight;
  readonly blockFlowLength: number; // 0 by default
}

interface SheetLayoutFixed {
  readonly layout: SheetLayout.Fixed;
  readonly sheetWidth: number;
  readonly sheetHeight: number;
  readonly fitStyle: FitStyle;
  readonly basePosition: ScrollBase;
  readonly enableMargin: boolean; // XXX : ここで良いのかな...?
}

export type ZoomableSheetLayoutData = SheetLayoutReflowable | SheetLayoutFixed;
export type SheetLayoutData = ZoomableSheetLayoutData | SheetLayoutReflowableDisableZooming;

export const isZoomingEnabledSheet = (data: SheetLayoutData): data is ZoomableSheetLayoutData => {
  return data.layout === SheetLayout.Fixed || data.fitStyle !== FitStyle.FitScreen;
};

export const isVerticalRightToLeftSheet = (data: SheetLayoutData): boolean => {
  if (data.layout === SheetLayout.Fixed) {
    return data.basePosition === ScrollBase.RightIfLargerThanScreen;
  }
  return data.fitStyle === FitStyle.FitHeight;
};

interface ZoomingParamsBase {
  readonly containerWidth: number;
  readonly containerHeight: number;
}

export type ZoomingParams = ZoomingParamsBase & ZoomableSheetLayoutData;

export interface ViewTransform {
  readonly scale: number;
  readonly translateX: number;
  readonly translateY: number;
}

export const initialViewScale = (params: ZoomingParams): number => {
  if (params.layout === SheetLayout.Fixed) {
    const { sheetWidth, sheetHeight, containerWidth, containerHeight, fitStyle } = params;
    if (fitStyle === FitStyle.FitScreen) {
      return Math.min(containerWidth / sheetWidth, containerHeight / sheetHeight);
    }
    return containerHeight / sheetHeight;
  }

  return 1;
};

export const initialViewPosition = (params: ZoomingParams, scale: number): Vector2D => {
  if (params.layout === SheetLayout.Fixed) {
    const { sheetWidth, sheetHeight, containerWidth, containerHeight, basePosition } = params;

    switch (basePosition) {
      case ScrollBase.Center:
        return {
          x: Math.floor(containerWidth - sheetWidth * scale) / 2,
          y: Math.floor(containerHeight - sheetHeight * scale) / 2,
        };
      case ScrollBase.LeftIfLargerThanScreen:
        return {
          x: Math.max(Math.floor(containerWidth - sheetWidth * scale) / 2, 0),
          y: Math.floor(containerHeight - sheetHeight * scale) / 2,
        };
      case ScrollBase.RightIfLargerThanScreen: {
        const diffWidth = containerWidth - sheetWidth * scale;
        const overflows = diffWidth < 0;
        return {
          x: overflows ? diffWidth : diffWidth / 2,
          y: Math.floor(containerHeight - sheetHeight * scale) / 2,
        };
      }
      default:
        // FIXME : This "default" clause should be removed for exhaustive check, but type narrowing
        // does not work well in "if" statements (TypeScript bug?).
        throw new Error('Unexpected type');
    }
  } else {
    switch (params.fitStyle) {
      case FitStyle.FitWidth: // horizontal-tb
        return { x: 0, y: 0 };
      case FitStyle.FitHeight: {
        // vertical-rl
        const { containerWidth, blockFlowLength } = params;
        return { x: containerWidth - blockFlowLength * scale, y: 0 };
      }
      default:
        // FIXME : see comment above
        throw new Error('Unexpected fitStyle');
    }
  }
};

export interface PanningRange {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

// The given margin must not have been scaled.
export const calculatePanningRange = (
  layoutParams: Readonly<ZoomingParams>,
  actualScale: number,
  margin: number,
): PanningRange => {
  let enableMargin = layoutParams.enableMargin;
  if (actualScale <= initialViewScale(layoutParams) + 0.001) {
    // 操作性向上のため、拡大している時のみマージン領域までスクロールできるようにする。
    // 拡大中かどうかの判定は初期ズームより大きいかによるが、小数誤差対策のため適当に0.001を加えておく。
    // TODO : できれば毎回initialViewScale計算せずに済ませて高速化したい
    enableMargin = false;
  }

  const scaledMargin = enableMargin ? (margin * actualScale) : 0;

  const { containerWidth, containerHeight } = layoutParams;
  let sheetWidth: number;
  let sheetHeight: number;

  if (layoutParams.layout === SheetLayout.Reflowable) {
    sheetWidth = containerWidth;
    sheetHeight = containerHeight;
    if (layoutParams.fitStyle === FitStyle.FitWidth) {
      // horizontal-tb
      sheetHeight = Math.max(sheetHeight, layoutParams.blockFlowLength);
    } else {
      // vertical-rl
      sheetWidth = Math.max(sheetWidth, layoutParams.blockFlowLength);
    }
  } else {
    sheetWidth = layoutParams.sheetWidth;
    sheetHeight = layoutParams.sheetHeight;
  }

  const marginBoxWidth = sheetWidth * actualScale + scaledMargin * 2;
  const marginBoxHeight = sheetHeight * actualScale + scaledMargin * 2;

  const diffWidth = containerWidth - marginBoxWidth;
  const diffHeight = containerHeight - marginBoxHeight;
  const overflowsX = diffWidth < 0;
  const overflowsY = diffHeight < 0;

  const minX = (overflowsX ? diffWidth : diffWidth / 2) + scaledMargin;
  const minY = (overflowsY ? diffHeight : diffHeight / 2) + scaledMargin;
  const maxX = (overflowsX ? 0 : diffWidth / 2) + scaledMargin;
  const maxY = (overflowsY ? 0 : diffHeight / 2) + scaledMargin;

  return { minX, minY, maxX, maxY };
};

 // ダイアログが画面外に飛んでいって戻せなくならないように、移動範囲を制限する
export const getMovableBounds = (
  bounds: Rectangle,
  rootContainerRect: Rectangle,
) => {
  const gripWidth = bounds.width;
  const gripHeight = popupTitlebarHeight;

  const { width: parentWidth, height: parentHeight } = rootContainerRect;
  const offset = popupMovableBoundsMargin;

  // ポップアップ座標はrootContainerRectの中での相対なので、rootContainerRect.x/yは不要
  return {
    left: -gripWidth + offset,
    top: -gripHeight + offset,
    right: parentWidth - offset,
    bottom: parentHeight - offset,
  };
};

export const getWindowRect = (): Rectangle => {
  return {
    x: 0,
    y: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
};
